/**
 * Payload table component for DEX reader workflow.
 * Displays fetched payloads in a sortable table with action buttons.
 * MVP: Shows payloads fetched during session (no backend list endpoint exists).
 */

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Get sort icon for a column header.
 * @param {string} column - Column name
 * @param {string} sortColumn - Currently sorted column
 * @param {string} sortDirection - Current sort direction ('asc' or 'desc')
 * @returns {string} Sort icon HTML
 */
function sortIcon(column, sortColumn, sortDirection) {
  if (column !== sortColumn) {
    // Neutral arrows for non-active columns
    return `<span class="text-gray-400 ml-1">&#8693;</span>`;
  }
  // Active column - show direction
  return sortDirection === 'asc'
    ? `<span class="text-blue-600 ml-1">&#8593;</span>`
    : `<span class="text-blue-600 ml-1">&#8595;</span>`;
}

/**
 * Sort payloads array by column and direction.
 * @param {Array} payloads - Array of payload objects
 * @param {string} column - Column to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted array (new copy)
 */
function sortPayloads(payloads, column, direction) {
  return [...payloads].sort((a, b) => {
    let aVal = a[column] || '';
    let bVal = b[column] || '';

    // String comparison
    const comparison = String(aVal).localeCompare(String(bVal));
    return direction === 'asc' ? comparison : -comparison;
  });
}

/**
 * Render the payload table into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {Array} [state.payloads=[]] - Array of payload objects
 * @param {string} [state.sortColumn='pk'] - Column to sort by
 * @param {string} [state.sortDirection='desc'] - Sort direction
 * @param {boolean} [state.loading=false] - Whether data is loading
 */
export function renderPayloadTable(container, state = {}) {
  const {
    payloads = [],
    sortColumn = 'pk',
    sortDirection = 'desc',
    loading = false
  } = state;

  // Loading state
  if (loading) {
    container.innerHTML = `
      <div class="border border-gray-200 rounded-lg overflow-hidden">
        <div class="animate-pulse">
          <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div class="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div class="divide-y divide-gray-200">
            ${[1, 2, 3].map(() => `
              <div class="px-4 py-3 flex gap-4">
                <div class="h-4 bg-gray-200 rounded flex-1"></div>
                <div class="h-4 bg-gray-200 rounded w-16"></div>
                <div class="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;
    return;
  }

  // Empty state
  if (payloads.length === 0) {
    container.innerHTML = `
      <div class="border border-gray-200 rounded-lg p-8 text-center">
        <p class="text-gray-500 text-sm">
          No payloads fetched yet. Enter payload IDs above to fetch.
        </p>
        <p class="text-gray-400 text-xs mt-2">
          <em>Note: MVP limitation - no backend list endpoint exists, so readers manually enter IDs to fetch payloads.</em>
        </p>
      </div>
    `;
    return;
  }

  // Sort payloads
  const sorted = sortPayloads(payloads, sortColumn, sortDirection);

  // Render table
  container.innerHTML = `
    <div class="border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm text-left">
        <thead class="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th
              class="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
              data-sort="pk"
            >
              Payload ID ${sortIcon('pk', sortColumn, sortDirection)}
            </th>
            <th
              class="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
              data-sort="status"
            >
              Status ${sortIcon('status', sortColumn, sortDirection)}
            </th>
            <th class="px-4 py-3">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          ${sorted.map(p => {
            const statusColor = p.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800';
            return `
              <tr class="hover:bg-gray-50">
                <td class="px-4 py-3 font-mono text-xs truncate max-w-xs" title="${escapeHtml(p.pk)}">
                  ${escapeHtml(p.pk)}
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 text-xs rounded font-medium ${statusColor}">
                    ${escapeHtml(p.status || 'unknown')}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <button
                    type="button"
                    class="text-blue-600 hover:underline text-sm mr-3"
                    data-action="decrypt"
                    data-payload-pk="${escapeHtml(p.pk)}"
                  >Decrypt</button>
                  <button
                    type="button"
                    class="text-gray-600 hover:underline text-sm"
                    data-action="complete"
                    data-payload-pk="${escapeHtml(p.pk)}"
                  >Complete</button>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Initialize the payload table with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onSort] - Called when column header clicked: (column) => void
 * @param {Function} [callbacks.onAction] - Called when action button clicked: (action, payloadPk) => void
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initPayloadTable(container, callbacks = {}) {
  const { onSort, onAction } = callbacks;
  const cleanupFunctions = [];

  // Wire sort header clicks
  const sortHeaders = container.querySelectorAll('[data-sort]');

  function handleSortClick(e) {
    const column = e.currentTarget.dataset.sort;
    if (column && onSort) {
      onSort(column);
    }
  }

  sortHeaders.forEach(th => {
    th.addEventListener('click', handleSortClick);
  });

  cleanupFunctions.push(() => {
    sortHeaders.forEach(th => {
      th.removeEventListener('click', handleSortClick);
    });
  });

  // Wire action button clicks
  const actionButtons = container.querySelectorAll('[data-action]');

  function handleActionClick(e) {
    const action = e.currentTarget.dataset.action;
    const payloadPk = e.currentTarget.dataset.payloadPk;
    if (action && payloadPk && onAction) {
      onAction(action, payloadPk);
    }
  }

  actionButtons.forEach(btn => {
    btn.addEventListener('click', handleActionClick);
  });

  cleanupFunctions.push(() => {
    actionButtons.forEach(btn => {
      btn.removeEventListener('click', handleActionClick);
    });
  });

  // Return combined cleanup function
  return function cleanup() {
    cleanupFunctions.forEach(fn => fn());
  };
}
